/**
 * CaseDetailDialog — fiche complète d'un cas de simulation, en popup redimensionnable.
 *
 * L'écran de choix du cas (TrainingPage) n'affiche plus que des capsules courtes :
 * le détail (offre, interlocuteurs, contexte, critères mis en avant) se lit ici,
 * puis « Jouer ce cas » enchaîne sur le choix de l'interlocuteur.
 *
 * Le contenu est rendu par l'appelant (`children`) : il dépend de helpers propres
 * à l'écran d'entraînement. Ce composant ne porte que la fenêtre — fond, Échap,
 * redimensionnement (`useResizable`, même mécanique que la fiche collaborateur).
 */
import type React from "react";
import { useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "./ui/button";
import { ResizeHandles } from "./ui/resize-handles";
import { useResizable } from "../hooks/useResizable";

interface CaseDetailDialogProps {
  title: ReactNode;
  /** Pastilles affichées au-dessus du titre (type B2B/B2C, provenance, conseillé). */
  badges?: ReactNode;
  subtitle?: ReactNode;
  /** Couleur de l'en-tête (charte : fond pastel du type de cas). */
  accentBg: string;
  children: ReactNode;
  /** Actions du pied de fenêtre (« Jouer ce cas », « Partir de ce cas »…). */
  footer: ReactNode;
  onClose: () => void;
}

export function CaseDetailDialog({ title, badges, subtitle, accentBg, children, footer, onClose }: CaseDetailDialogProps) {
  const { t } = useTranslation();
  const { containerRef, onPointerDown, boxStyle } = useResizable({
    minWidth: 360,
    minHeight: 320,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 32 : 1200,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 32 : 900,
  });
  // Le clic sur le fond ne ferme que s'il a COMMENCÉ sur le fond : relâcher la souris
  // hors de la fenêtre à la fin d'un redimensionnement ne doit pas la fermer.
  const pressStartedOnBackdrop = useRef(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape" || e.defaultPrevented) return;
    e.preventDefault();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1B2440]/50 backdrop-blur-[2px] p-2"
      onPointerDown={(e) => { pressStartedOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        const fromBackdrop = pressStartedOnBackdrop.current && e.target === e.currentTarget;
        pressStartedOnBackdrop.current = false;
        if (fromBackdrop && !document.body.style.cursor) onClose();
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="case-detail-title"
        tabIndex={-1}
        autoFocus
        onKeyDown={handleKeyDown}
        className="relative bg-card text-card-foreground rounded-2xl shadow-[0_24px_60px_-12px_rgba(27,36,64,0.45)] ring-1 ring-black/5 flex flex-col overflow-hidden outline-none"
        style={boxStyle(760, 680)}
        data-testid="case-detail-dialog"
      >
        <div className="flex items-start gap-3 px-5 pt-5 pb-4 border-b border-slate-200" style={{ backgroundColor: accentBg + "30" }}>
          <div className="min-w-0 flex-1">
            {badges && <div className="flex items-center gap-2 flex-wrap mb-1.5">{badges}</div>}
            <h2 id="case-detail-title" className="font-['Sora'] font-bold text-lg text-[#384872] break-words">{title}</h2>
            {subtitle && <div className="mt-1 text-sm text-[#384872]/80 break-words">{subtitle}</div>}
          </div>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 rounded-full bg-white/70 hover:bg-white shadow-sm" aria-label={t("common.close")} onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50/40">{children}</div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-white">{footer}</div>

        <ResizeHandles onPointerDown={onPointerDown} />
      </div>
    </div>
  );
}
